import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { ROUTES } from '@utils/constants';
import './styles/appheader.css';

interface AppHeaderProps {
  /**
   * Optional: Show back button instead of logo navigation
   * If true, clicking logo will go back instead of to dashboard
   */
  showBack?: boolean;
  /**
   * Optional: Custom back handler
   * If provided, will be called instead of default navigation
   */
  onBack?: () => void;
  /**
   * Optional: Show logout button (typically only on Dashboard)
   */
  showLogout?: boolean;
  /**
   * Optional: Custom logout handler
   * If provided, will be called instead of default logout
   */
  onLogout?: () => void;
}

/**
 * Reusable App Header Component
 * 
 * Features:
 * - Yellow theme background (#f5a623)
 * - Zero Moblt logo on the left (20px from left, 20px vertical padding)
 * - Navigates to Dashboard on logo click (only if logged in)
 * - Optional back button mode
 * - Optional logout button (visible only on Dashboard)
 * 
 * Usage:
 * <AppHeader />
 * <AppHeader showBack onBack={() => navigate(-1)} />
 * <AppHeader showLogout onLogout={handleLogout} />
 */
export const AppHeader: React.FC<AppHeaderProps> = ({ showBack = false, onBack, showLogout = false, onLogout }) => {
  const navigate = useNavigate();
  const { isAuthenticated, logout } = useAuth();

  const handleLogoClick = () => {
    if (showBack && onBack) {
      onBack();
    } else if (isAuthenticated) {
      navigate(ROUTES.WELCOME);
    }
  };

  const handleLogout = () => {
    if (onLogout) {
      onLogout();
    } else {
      logout();
    }
  };

  return (
    <header className="app-header">
      <button
        className="app-header__logo-btn"
        onClick={handleLogoClick}
        aria-label="Zero Moblt - Go to Dashboard"
        disabled={!isAuthenticated && !showBack}
      >
        <img
          src="/zero-logo.png"
          alt="Zero Moblt"
          className="app-header__logo-img"
        />
      </button>

      {showLogout && isAuthenticated && (
        <button
          className="app-header__logout-btn"
          onClick={handleLogout}
          aria-label="Logout"
        >
          Logout
        </button>
      )}
    </header>
  );
};

export default AppHeader;
